import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Package, Sparkles, Repeat, DollarSign, Calendar, MapPin } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import TikTokIcon from "@/components/TikTokIcon";
import api from "@/lib/api";
import { TIKTOK_URL, TIKTOK_HANDLE, INSTAGRAM_URL, INSTAGRAM_HANDLE, FACEBOOK_URL, FACEBOOK_HANDLE } from "@/lib/brand";
import { Instagram, Facebook } from "lucide-react";
import { getShowSchedule } from "@/lib/shows";

const CTA_LABEL_DEFAULT = "Event details";

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const { featured: NEXT_EVENT, upcoming: ALSO_UPCOMING } = useMemo(
    () => getShowSchedule(),
    []
  );

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="text-white">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-800">
        {/* Background logo — big & centered */}
        <img
          src="/brand/logo-red.png"
          alt=""
          aria-hidden="true"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[140%] sm:h-[160%] w-auto opacity-[0.12] pointer-events-none select-none"
        />
        {/* Soft fade to keep edges & queue card readable */}
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/40 to-white" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-white" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 md:px-16 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-10 items-center">
          <div className="flex flex-col justify-center max-w-4xl">
            <div className="inline-flex items-center gap-2 self-start bg-slate-900/80 backdrop-blur border border-slate-800 px-3 py-1.5 rounded-full mb-6">
              <span className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-300">
                A group of Alberta-based vendors
              </span>
            </div>
            <div className="text-xs font-mono tracking-[0.3em] uppercase text-yellow-400 mb-3">
              // The Washed Up Arena
            </div>
            <h1 className="font-[Outfit] text-4xl sm:text-5xl lg:text-7xl tracking-tighter font-black leading-[0.95]">
              We <span className="text-yellow-400">buy</span>,{" "}
              <span className="text-yellow-400">sell</span> &{" "}
              <span className="text-yellow-400">trade</span><br />
              Pokemon cards.
            </h1>
            <p className="mt-6 text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed">
              Alberta-based vendor. Bring us your collection or shop ours —
              cash offers at 70–75% market, trade credit at 80%. See you at the next show.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/buy"
                data-testid="hero-buy-cta"
                className="inline-flex items-center gap-2 bg-yellow-400 text-slate-950 font-bold px-6 py-3 rounded-md uppercase tracking-wide hover:bg-yellow-300 transition-colors shadow-[0_0_25px_rgba(250,204,21,0.25)]"
              >
                <DollarSign className="w-5 h-5" />
                Sell to us
              </Link>
              <Link
                to="/shop"
                data-testid="hero-shop-cta"
                className="inline-flex items-center gap-2 bg-slate-900 text-white border border-slate-700 font-medium px-6 py-3 rounded-md hover:bg-slate-800 transition-colors"
              >
                Browse inventory <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/trade"
                data-testid="hero-trade-cta"
                className="inline-flex items-center gap-2 bg-slate-900 text-white border border-slate-700 font-medium px-6 py-3 rounded-md hover:bg-slate-800 transition-colors"
              >
                Request a trade <Repeat className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-6">
              <a
                href={TIKTOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-tiktok-cta"
                className="inline-flex items-center gap-2 text-slate-300 hover:text-yellow-400 text-sm"
              >
                <TikTokIcon className="w-4 h-4" />
                Follow @{TIKTOK_HANDLE} for restock updates
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-xs text-slate-400 font-mono uppercase tracking-wider">
              <div className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-yellow-400" /> Cash 70–75%</div>
              <div className="flex items-center gap-2"><Repeat className="w-4 h-4 text-yellow-400" /> Trade 80%</div>
              <div className="flex items-center gap-2"><Package className="w-4 h-4 text-yellow-400" /> Sealed &amp; singles</div>
            </div>
          </div>

          {/* Big logo — right side of hero */}
          <div
            className="hidden lg:flex justify-center items-center"
            data-testid="hero-logo"
            style={{ position: "relative", zIndex: 100 }}
          >
            <img
              src="/brand/logo-red.png"
              alt="Washed Up Trainers"
              className="w-[500px] xl:w-[640px] h-auto drop-shadow-[0_12px_40px_rgba(153,27,27,0.5)]"
              style={{
                position: "relative",
                zIndex: 100,
                filter: "brightness(0.55) saturate(1.4) contrast(1.15)",
              }}
            />
          </div>
        </div>
      </section>

      {/* Upcoming shows */}
      {NEXT_EVENT && (
      <section
        data-testid="upcoming-shows-section"
        className="max-w-7xl mx-auto px-4 sm:px-8 md:px-16 pt-16 pb-4"
      >
        <div className="text-xs tracking-[0.25em] uppercase font-mono text-yellow-400 mb-6">
          // Upcoming shows
        </div>

        {/* Featured next event */}
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 items-center bg-red-600 border border-red-700 rounded-2xl overflow-hidden shadow-[0_8px_40px_rgba(220,38,38,0.25)]">
          {NEXT_EVENT.link ? (
            <a
              href={NEXT_EVENT.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="next-event-image-link"
              className="block group relative bg-slate-950"
            >
              <NextEventMedia event={NEXT_EVENT} />
            </a>
          ) : (
            <div data-testid="next-event-image-link" className="block relative bg-slate-950">
              <NextEventMedia event={NEXT_EVENT} />
            </div>
          )}
          <div className="p-6 sm:p-8 lg:pr-10 text-white">
            <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-white/80 mb-2">
              Live IRL event
            </div>
            <h2 className="font-[Outfit] text-2xl sm:text-3xl font-black leading-tight text-white">
              {NEXT_EVENT.name}
            </h2>
            <div className="mt-4 space-y-2 text-sm text-white/90">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-white" />
                <span className="font-mono">{NEXT_EVENT.dates}</span>
              </div>
              {NEXT_EVENT.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-white" />
                  <span>{NEXT_EVENT.location}</span>
                </div>
              )}
            </div>
            {NEXT_EVENT.link && (
              <a
                href={NEXT_EVENT.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="next-event-cta"
                className="mt-6 inline-flex items-center gap-2 bg-white text-red-700 font-bold px-5 py-2.5 rounded-md uppercase tracking-wide text-sm hover:bg-red-50 transition-colors"
              >
                {NEXT_EVENT.cta_label || "Event details"} <ArrowRight className="w-4 h-4" />
              </a>
            )}
            <p className="mt-4 text-xs text-white/80 leading-relaxed">
              {NEXT_EVENT.blurb}
            </p>
          </div>
        </div>

        {/* Also upcoming — smaller cards */}
        {ALSO_UPCOMING.length > 0 && (
        <div className="mt-8">
          <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-red-600 font-bold mb-4">
            Also coming up
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ALSO_UPCOMING.map((ev) => (
              <a
                key={ev.slug || ev.name}
                href={ev.link || "#"}
                target={ev.link ? "_blank" : undefined}
                rel={ev.link ? "noopener noreferrer" : undefined}
                data-testid={`upcoming-event-${ev.slug}`}
                className="group bg-red-600 border border-red-700 hover:border-red-800 rounded-2xl overflow-hidden transition-all hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(220,38,38,0.25)] flex flex-col text-white"
              >
                <div className="bg-slate-950 border-b border-slate-800 flex items-center justify-center overflow-hidden">
                  {ev.type === "instagram" ? (
                    <iframe
                      title={ev.name}
                      src={ev.embed_url}
                      className="w-full block"
                      style={{ height: 500, border: 0 }}
                      scrolling="no"
                      allow="encrypted-media"
                    />
                  ) : (
                    <img
                      src={ev.image_url}
                      alt={ev.name}
                      loading="lazy"
                      className="w-full h-auto block group-hover:brightness-110 transition-all"
                    />
                  )}
                </div>
                <div className="p-5 flex flex-col flex-1 text-white">
                  <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-white/80 mb-1">
                    {ev.badge}
                  </div>
                  <h3 className="font-[Outfit] text-lg font-black text-white leading-tight">
                    {ev.name}
                  </h3>
                  <div className="mt-3 space-y-1.5 text-sm text-white/90">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-white" />
                      <span className="font-mono">{ev.dates}</span>
                    </div>
                    {ev.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-white" />
                        <span>{ev.location}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-auto pt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-white font-bold group-hover:opacity-90">
                    {ev.cta} <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
        )}
      </section>
      )}

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 md:px-16 py-20">
        <div className="text-xs tracking-[0.25em] uppercase font-mono text-yellow-400 mb-3">
          // How it works
        </div>
        <h2 className="font-[Outfit] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-2xl">
          Three ways to work with us.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Sell to us", d: "Snap photos of your cards, tell us what you want, we send back a cash or trade-credit offer.", to: "/buy", cta: "Get an offer" },
            { n: "02", t: "Shop our inventory", d: "Sealed packs, ETBs, boxes, plus graded slabs and singles from the vault.", to: "/shop", cta: "Browse" },
            { n: "03", t: "Trade at 80%", d: "Have something you'd rather trade toward? Send us the cards and what you're seeking.", to: "/trade", cta: "Request a trade" },
          ].map((s) => (
            <Link
              key={s.n}
              to={s.to}
              data-testid={`step-${s.n}`}
              className="group bg-slate-900 border border-slate-800 hover:border-yellow-400/50 p-6 rounded-xl transition-all"
            >
              <div className="font-mono text-yellow-400 text-3xl font-black">{s.n}</div>
              <h3 className="mt-2 text-lg font-bold">{s.t}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{s.d}</p>
              <div className="mt-4 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-yellow-400 group-hover:text-yellow-300">
                {s.cta} <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 md:px-16 py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase font-mono text-yellow-400 mb-3">
              // Featured drops
            </div>
            <h2 className="font-[Outfit] text-3xl sm:text-4xl font-bold tracking-tight">
              In stock right now
            </h2>
          </div>
          <Link
            to="/shop"
            data-testid="featured-view-all"
            className="hidden sm:inline-flex items-center gap-1 text-yellow-400 text-sm font-mono uppercase tracking-wider hover:text-yellow-300"
          >
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {products.slice(0, 4).map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* Socials CTA banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 md:px-16 py-20">
        <div
          data-testid="socials-cta"
          className="bg-gradient-to-br from-slate-50 to-white border border-red-500/30 rounded-2xl p-8 sm:p-12 relative overflow-hidden shadow-lg"
        >
          <Sparkles className="absolute top-6 right-6 w-12 h-12 text-red-500/20" />
          <div className="text-xs tracking-[0.25em] uppercase font-mono text-red-600 mb-3">
            // Follow along
          </div>
          <h3 className="font-[Outfit] text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight max-w-2xl text-slate-900">
            Follow us on the socials!
          </h3>
          <p className="mt-3 text-slate-600 max-w-xl leading-relaxed">
            Behind-the-booth looks, new arrivals, show day updates and the odd
            giveaway. Tap in and don&apos;t miss a drop.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={TIKTOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="socials-tiktok-cta"
              className="inline-flex items-center gap-2 bg-slate-950 text-white font-bold px-5 py-3 rounded-md uppercase tracking-wide hover:bg-slate-800 transition-colors text-sm"
            >
              <TikTokIcon className="w-4 h-4" />
              TikTok · @{TIKTOK_HANDLE}
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="socials-instagram-cta"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 text-white font-bold px-5 py-3 rounded-md uppercase tracking-wide hover:brightness-110 transition-all text-sm"
            >
              <Instagram className="w-4 h-4" />
              Instagram · @{INSTAGRAM_HANDLE}
            </a>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="socials-facebook-cta"
              className="inline-flex items-center gap-2 bg-[#1877F2] text-white font-bold px-5 py-3 rounded-md uppercase tracking-wide hover:bg-[#1465d8] transition-colors text-sm"
            >
              <Facebook className="w-4 h-4" />
              Facebook · {FACEBOOK_HANDLE}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function NextEventMedia({ event }) {
  if (!event) return null;
  if (event.type === "instagram") {
    return (
      <iframe
        title={event.name}
        src={event.embed_url}
        className="w-full lg:w-[400px] block"
        style={{ height: 560, border: 0 }}
        scrolling="no"
        allow="encrypted-media"
      />
    );
  }
  return (
    <img
      src={event.image_url}
      alt={event.name}
      loading="lazy"
      className="w-full lg:w-[400px] h-auto block hover:brightness-110 transition-all"
    />
  );
}
